import React from 'react';
import Button from '../common/Button';

export default function AdminTourForm({
    t,
    i18n,
    newTour,
    setNewTour,
    agencies,
    propertyIds,
    handleNewTourSubmit,
    uploadProgress,
    fileInputRef
}) {
    return (
        <div className="bg-bg-card border border-gold/20 shadow rounded-lg p-6">
            <h3 className="text-lg font-bold text-gold mb-6">
                {t('uploadTour')}
            </h3>
            <form onSubmit={handleNewTourSubmit} className="space-y-4">
                {/* Agency Selector */}
                <div>
                    <label className="block text-sm font-medium text-text-muted mb-1">{t('agencyName')} <span className="text-danger-500">*</span></label>
                    <select
                        className="bg-bg-soft border border-gold/20 text-text rounded px-3 py-2 w-full focus:ring-gold focus:border-gold placeholder-textMuted/50"
                        value={newTour.agency_id}
                        onChange={e => setNewTour({ ...newTour, agency_id: e.target.value })}
                        required
                    >
                        <option value="">{t('selectAgency')}</option>
                        {(agencies || []).map(a => <option key={a.id} value={a.id}>{i18n.language === 'ar' ? (a.name_ar || a.name) : (a.name_en || a.name)}</option>)}
                    </select>
                </div>

                {/* Checkbox for New Property */}
                <div>
                    <label className="flex items-center mb-2 cursor-pointer">
                        <input
                            type="checkbox"
                            className="mr-2 text-gold focus:ring-gold bg-bg-card border-gold/30 rounded"
                            checked={newTour.isNewPropertyId}
                            onChange={e => setNewTour({ ...newTour, isNewPropertyId: e.target.checked, property_id: '', title: '' })}
                            disabled={!newTour.agency_id}
                        />
                        <span className="p-2 sm:px-4 sm:py-2 text-sm font-medium text-gold bg-bg-soft hover:bg-gold hover:text-primary border border-gold/20 rounded-lg transition-all duration-300 flex items-center gap-2">{t('addNewPropertyId')}</span>
                    </label>
                </div>

                {newTour.isNewPropertyId ? (
                    /* NEW PROPERTY FORM */
                    <>
                        {/* Property Name */}
                        <div>
                            <label className="block text-sm font-medium text-text-muted mb-1">{t('propertyName')} <span className="text-danger-500">*</span></label>
                            <input
                                type="text"
                                placeholder={t('propertyName')}
                                className="bg-bg-soft border border-gold/20 text-text rounded px-3 py-2 w-full focus:ring-gold focus:border-gold placeholder-textMuted/50 disabled:opacity-50"
                                value={newTour.title || ''}
                                onChange={e => setNewTour({ ...newTour, title: e.target.value })}
                                disabled={!newTour.agency_id}
                                required
                            />
                        </div>

                        {/* New Property ID */}
                        <div>
                            <label className="block text-sm font-medium text-text-muted mb-1">{t('newPropertyId')} <span className="text-danger-500">*</span></label>
                            <input
                                type="text"
                                placeholder={t('enterNewPropertyId')}
                                className="bg-bg-soft border border-gold/20 text-text rounded px-3 py-2 w-full focus:ring-gold focus:border-gold placeholder-textMuted/50 disabled:opacity-50"
                                value={newTour.property_id || ''}
                                onChange={e => setNewTour({ ...newTour, property_id: e.target.value })}
                                disabled={!newTour.agency_id}
                                required
                            />
                        </div>
                    </>
                ) : (
                    /* EXISTING PROPERTY FORM */
                    <>
                        {/* Existing Property ID Dropdown */}
                        <div>
                            <label className="block text-sm font-medium text-text-muted mb-1">{t('propertyId')} <span className="text-danger-500">*</span></label>
                            <select
                                className="bg-bg-soft border border-gold/20 text-text rounded px-3 py-2 w-full focus:ring-gold focus:border-gold placeholder-textMuted/50 disabled:opacity-50"
                                value={newTour.property_id || ''}
                                onChange={e => setNewTour({ ...newTour, property_id: e.target.value })}
                                disabled={!newTour.agency_id}
                                required
                            >
                                <option value="">{t('selectPropertyId')}</option>
                                {(propertyIds || []).map(id => <option key={id} value={id}>{id}</option>)}
                            </select>
                        </div>
                    </>
                )}

                {/* Tour Type Selector */}
                <div>
                    <label className="block text-sm font-medium text-text-muted mb-2">{t('tourType') || "Tour Type"}</label>
                    <div className="flex gap-4">
                        <label className="flex items-center cursor-pointer">
                            <input
                                type="radio"
                                name="tourType"
                                value="zip"
                                checked={newTour.type === 'zip'}
                                onChange={() => setNewTour({ ...newTour, type: 'zip' })}
                                className="mr-2 text-gold focus:ring-gold bg-bg-card border-gold/30"
                                disabled={!newTour.agency_id}
                            />
                            <span className="text-text">{t('tourZipFile') || "ZIP File"}</span>
                        </label>
                        <label className="flex items-center cursor-pointer">
                            <input
                                type="radio"
                                name="tourType"
                                value="url"
                                checked={newTour.type === 'url'}
                                onChange={() => setNewTour({ ...newTour, type: 'url' })}
                                className="mr-2 text-gold focus:ring-gold bg-bg-card border-gold/30"
                                disabled={!newTour.agency_id}
                            />
                            <span className="text-text">{t('externalUrl') || "External URL (Kuula)"}</span>
                        </label>
                    </div>
                </div>

                {/* Conditional Input: File or URL */}
                {newTour.type === 'zip' ? (
                    <div key="zip-input">
                        <label className="block text-sm font-medium text-text-muted mb-1">{t('tourFileZip')} <span className="text-danger-500">*</span></label>
                        <input
                            type="file"
                            ref={fileInputRef}
                            accept=".zip"
                            className="bg-bg-soft border border-gold/20 text-text-muted rounded px-3 py-2 w-full file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-gold-gradient file:text-white hover:file:opacity-90 disabled:opacity-50"
                            onChange={e => setNewTour({ ...newTour, file: e.target.files[0] })}
                            disabled={!newTour.agency_id}
                            required={newTour.type === 'zip'}
                        />
                        <p className="text-xs text-text-muted mt-1">{t('zipFileHelp')}</p>
                    </div>
                ) : (
                    <div key="url-input">
                        <label className="block text-sm font-medium text-text-muted mb-1">{t('tourUrl')} <span className="text-danger-500">*</span></label>
                        <input
                            type="url"
                            placeholder="https://kuula.co/share/..."
                            className="bg-bg-soft border border-gold/20 text-text rounded px-3 py-2 w-full focus:ring-gold focus:border-gold placeholder-textMuted/50 disabled:opacity-50"
                            value={newTour.tour_url || ''}
                            onChange={e => setNewTour({ ...newTour, tour_url: e.target.value })}
                            disabled={!newTour.agency_id}
                            required={newTour.type === 'url'}
                        />
                    </div>
                )}

                <Button
                    type="submit"
                    variant="primary"
                    className="w-full"
                    disabled={uploadProgress > 0 || !newTour.agency_id}
                >
                    {uploadProgress > 0 ? `${t('uploading')} ${uploadProgress}%` : t('uploadTour')}
                </Button>

                {uploadProgress > 0 && (
                    <div className="w-full bg-bg-card border border-gold/20 rounded-full h-2.5 mt-2">
                        <div className="bg-gold h-2.5 rounded-full" style={{ width: `${uploadProgress}%` }}></div>
                    </div>
                )}
            </form>
        </div>
    );
}
